import { useState, useEffect } from "react";
import * as Yup from "yup";
import { showToast } from "../../../../components/CustomToast";
import { OutlinedInput } from "../../../../components/OutlinedInput";
import { FormModal } from "../../../../components/FormModal";
import api from "../../../../services/api";
import { typesErrors } from "../../../../utils/validateFormFields/yupErrors";
import validateFormFields from "../../../../utils/validateFormFields";
import { i18n } from "../../../../localization/i18n";
import { ITransaction } from "../../../Financial/components/Transactions/dtos";

interface Props {
  open?: boolean;
  licensee_id: string;
  customerName?: string;
  transaction?: ITransaction | null;
  onHandleCloseRequest: () => void;
  onSuccess?: () => void;
}

interface IData {
  description: string;
  ammount: number;
  licensee_id: string;
  type: string;
  transaction_date: Date;
}

export function AddFixcoinModal({
  open = true,
  licensee_id,
  customerName,
  transaction,
  onHandleCloseRequest,
  onSuccess,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const isEditing = Boolean(transaction?.id);

  const [data, setData] = useState<IData>({
    description: transaction?.description || "",
    ammount: Number(transaction?.ammount || 0),
    licensee_id,
    type: transaction?.type || "income",
    transaction_date: transaction?.transaction_date ? new Date(transaction.transaction_date) : new Date(),
  });

  useEffect(() => {
    if (transaction) {
      setData({
        description: transaction.description || "",
        ammount: Number(transaction.ammount || 0),
        licensee_id,
        type: transaction.type || "income",
        transaction_date: transaction.transaction_date ? new Date(transaction.transaction_date) : new Date(),
      });
    } else {
      setData({
        description: "",
        ammount: 0,
        licensee_id,
        type: "income",
        transaction_date: new Date(),
      });
    }
  }, [transaction, licensee_id]);

  async function handleSaveTransaction() {
    try {
      const yup_validation = Yup.object().shape({
        description: Yup.string().required(typesErrors.required),
        ammount: Yup.number().moreThan(0).required(typesErrors.required),
      });

      const validation = await validateFormFields(data, yup_validation, {
        description: "",
        ammount: "",
      });

      if (validation.status === 400) {
        setErrors(validation.errors);
        showToast({
          type: "error",
          message: i18n.t("form_errors.alert"),
        });
        return;
      }

      setErrors({});
      setLoading(true);

      if (isEditing && transaction?.id) {
        await api.put(`/transactions/${transaction.id}`, {
          description: data.description,
          ammount: data.ammount,
        });
        showToast({
          type: "success",
          message: "Transação atualizada com sucesso",
        });
      } else {
        await api.post("/transactions", data);
        showToast({
          type: "success",
          message: "Fixcoin adicionado a carteira do licenciado",
        });
      }

      if (onSuccess) {
        onSuccess();
      } else {
        onHandleCloseRequest();
      }
    } catch (error) {
      showToast({
        type: "error",
        message: isEditing
          ? "Não foi possível atualizar a transação"
          : "Não foi possível adicionar Fixcoin",
      });
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <FormModal
      open={open}
      onRequestClose={onHandleCloseRequest}
      title={isEditing ? "Editar FixCoins" : "Adicionar FixCoins"}
      subtitle={
        customerName
          ? isEditing
            ? `Edite a transação de FixCoins de ${customerName}`
            : `Adicione saldo em FixCoins para ${customerName}`
          : undefined
      }
      width={450}
      onSave={handleSaveTransaction}
      isSaveLoading={loading}
      saveText={isEditing ? "Salvar" : "Adicionar"}
      cancelText="Cancelar"
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "16px", padding: "16px 0" }}>
        <OutlinedInput
          error={errors?.description}
          handleChange={(event) =>
            setData((prevState) => ({
              ...prevState,
              description: event,
            }))
          }
          inputName="description"
          label="Descrição"
          value={data.description}
          type="text"
        />
        <OutlinedInput
          error={errors?.ammount}
          handleChange={(event) =>
            setData((prevState) => ({
              ...prevState,
              ammount: Number(event),
            }))
          }
          inputName="ammount"
          label="Quantidade"
          value={data.ammount || ""}
          type="number"
        />
      </div>
    </FormModal>
  );
}
